/**
 * OutputToolbar — slim control row rendered above a generated-output area.
 *
 * Lets the editor:
 *   - change font family / size of the output area (cosmetic, client-side only)
 *   - toggle inline editing of the output (contentEditable)
 *   - save or discard their edits
 *   - view a word-level diff between the original AI draft and their saved edit
 *
 * Props:
 *   fontSize        number   — current font size in px
 *   setFontSize     fn(number)
 *   fontFamily      string   — current font family
 *   setFontFamily   fn(string)
 *   isEditing       boolean  — whether the output area is currently editable
 *   onToggleEdit    fn()     — called when "Edit" / "Editing…" is clicked
 *   onSaveEdit      fn()     — called when "Save Edit" is clicked
 *   onDiscardEdit   fn()     — called when "Discard" is clicked
 *   hasUnsavedEdit  boolean  — true when the user has edited but not yet saved
 *   hasSavedEdit    boolean  — true when an edit was saved (enables "Show Changes")
 *   originalText    string   — original AI-generated text, for diffing
 *   editedText      string   — current (saved) edited text, for diffing
 */

import React, { useState } from 'react';
import {
  FiMinus,
  FiPlus,
  FiEdit3,
  FiCheck,
  FiX,
  FiEye,
} from 'react-icons/fi';
import { useT } from '../../utils/i18n';

export const FONT_FAMILIES = [
  'Noto Sans Telugu',
  'Gautami',
  'Vemana2000',
  'Priya Ank',
  'Priya B',
  'Arial',
  'Georgia',
  'Courier New',
];

const MIN_FONT_SIZE = 12;
const MAX_FONT_SIZE = 36;
const FONT_STEP = 2;

// ─── Simple LCS-based word-level diff ───────────────────────────────────────
// Returns an array of { type: 'same' | 'add' | 'del', value: string } tokens.
function wordDiff(originalText = '', editedText = '') {
  const a = (originalText || '').split(/(\s+)/).filter((t) => t !== '');
  const b = (editedText || '').split(/(\s+)/).filter((t) => t !== '');

  const n = a.length;
  const m = b.length;

  // LCS length table
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const result = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      result.push({ type: 'same', value: a[i] });
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      result.push({ type: 'del', value: a[i] });
      i++;
    } else {
      result.push({ type: 'add', value: b[j] });
      j++;
    }
  }
  while (i < n) { result.push({ type: 'del', value: a[i] }); i++; }
  while (j < m) { result.push({ type: 'add', value: b[j] }); j++; }

  return result;
}

// ─── Diff Modal ──────────────────────────────────────────────────────────────

const DiffModal = ({ originalText, editedText, onClose }) => {
  const t = useT();
  const tokens = wordDiff(originalText, editedText);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-800">{t('toolbar.changesVsOriginal')}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            title={t('toolbar.close')}
          >
            <FiX size={16} />
          </button>
        </div>

        <div className="px-5 py-3 flex items-center gap-4 text-xs text-gray-500 border-b border-gray-100">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded bg-green-200 border border-green-400" />
            {t('toolbar.addedByEditor')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded bg-red-200 border border-red-400" />
            {t('toolbar.removedFromDraft')}
          </span>
        </div>

        <div className="overflow-y-auto px-5 py-4 text-sm leading-relaxed whitespace-pre-wrap">
          {tokens.map((tok, idx) => {
            if (tok.type === 'same') {
              return <span key={idx}>{tok.value}</span>;
            }
            if (tok.type === 'add') {
              return (
                <span key={idx} className="bg-green-100 text-green-800 rounded px-0.5">
                  {tok.value}
                </span>
              );
            }
            return (
              <span key={idx} className="bg-red-100 text-red-700 line-through rounded px-0.5">
                {tok.value}
              </span>
            );
          })}
          {tokens.length === 0 && (
            <span className="text-gray-400 italic">{t('toolbar.noContent')}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── OutputToolbar ───────────────────────────────────────────────────────────

export const OutputToolbar = ({
  fontSize,
  setFontSize,
  fontFamily,
  setFontFamily,
  isEditing,
  onToggleEdit,
  onSaveEdit,
  onDiscardEdit,
  hasUnsavedEdit,
  hasSavedEdit = false,
  originalText = '',
  editedText = '',
}) => {
  const t = useT();
  const [showDiff, setShowDiff] = useState(false);

  const decreaseFont = () => setFontSize(Math.max(MIN_FONT_SIZE, fontSize - FONT_STEP));
  const increaseFont = () => setFontSize(Math.min(MAX_FONT_SIZE, fontSize + FONT_STEP));

  return (
    <div className="flex items-center gap-2 flex-wrap bg-gray-50/80 border border-gray-200 rounded-lg px-2.5 py-1.5">

      {/* Font family */}
      <select
        value={fontFamily}
        onChange={(e) => setFontFamily(e.target.value)}
        className="text-xs border border-gray-200 rounded-md bg-white px-2 py-1 text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-400"
        title={t('toolbar.fontFamily')}
        style={{ fontFamily }}
      >
        {FONT_FAMILIES.map((f) => (
          <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
        ))}
      </select>

      {/* Font size */}
      <div className="flex items-center gap-1">
        <button
          onClick={decreaseFont}
          disabled={fontSize <= MIN_FONT_SIZE}
          title={t('toolbar.decreaseFont')}
          className="w-6 h-6 flex items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-40 text-xs font-semibold"
        >
          <FiMinus size={11} />
        </button>
        <span className="text-xs text-gray-500 w-9 text-center select-none">{fontSize}px</span>
        <button
          onClick={increaseFont}
          disabled={fontSize >= MAX_FONT_SIZE}
          title={t('toolbar.increaseFont')}
          className="w-6 h-6 flex items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:opacity-40 text-xs font-semibold"
        >
          <FiPlus size={11} />
        </button>
      </div>

      <div className="h-4 w-px bg-gray-300" />

      {/* Edit toggle */}
      {!isEditing ? (
        <button
          onClick={onToggleEdit}
          className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-primary-600 px-2 py-1 rounded-md hover:bg-gray-100"
          title={t('toolbar.editOutput')}
        >
          <FiEdit3 size={12} />
          {t('toolbar.edit')}
        </button>
      ) : (
        <>
          <span className="flex items-center gap-1.5 text-xs font-medium text-primary-600 px-2 py-1">
            <FiEdit3 size={12} className="animate-pulse" />
            {t('toolbar.editing')}
          </span>

          <button
            onClick={onSaveEdit}
            className="flex items-center gap-1.5 text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700 px-2.5 py-1 rounded-md"
            title={t('toolbar.saveYourEdit')}
          >
            <FiCheck size={12} />
            {t('toolbar.saveEdit')}
          </button>

          <button
            onClick={onDiscardEdit}
            className="flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 px-2.5 py-1 rounded-md border border-gray-200 bg-white hover:bg-gray-50"
            title={t('toolbar.discardYourEdit')}
          >
            <FiX size={12} />
            {t('toolbar.discard')}
          </button>
        </>
      )}

      {/* Show Changes — only when there's a saved edit and nothing unsaved pending */}
      {!hasUnsavedEdit && hasSavedEdit && !isEditing && (
        <button
          onClick={() => setShowDiff(true)}
          className="ml-1 flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-primary-600 px-2 py-1 rounded-md hover:bg-gray-100"
          title={t('toolbar.showChangesTitle')}
        >
          <FiEye size={12} />
          {t('toolbar.showChanges')}
        </button>
      )}

      {showDiff && (
        <DiffModal
          originalText={originalText}
          editedText={editedText}
          onClose={() => setShowDiff(false)}
        />
      )}
    </div>
  );
};

export default OutputToolbar;
